/**
 * useProcessList — единая сборка списка процессов (заявки/задачи/участие) для
 * Processes и Home «Все процессы». Перенесено из app/(portal)/processes.tsx
 * (~2295-2320, source-свитч вкладок ~2367) и app/(portal)/home.tsx (~296-321):
 * оба экрана дублировали reqVM/taskVM-маппинг и статус-подсчёт инлайн в теле
 * рендера без useMemo — пересчёт на каждый ре-рендер экрана.
 *
 * `opts.participating` включает GET /edo/participating (нужен «Все» и
 * «Согласую / подписываю» на Processes и карточке «Все процессы» на Home — иначе
 * статус-счётчики на главной нули, пока кэш не прогреет экран Processes).
 */
import { useMemo } from 'react'
import { useMyRequests, useMyTasks, useMyParticipating } from './useEdo'
import { reqVM, taskVM, statusCounts, mergeByDoc, signingOf } from './processVM'

export function useProcessList(opts?: { participating?: boolean }) {
  const requests = useMyRequests()
  const tasks = useMyTasks()
  const participating = useMyParticipating(opts?.participating ?? false)

  const reqVMs = useMemo(() => (requests.data ?? []).map(reqVM), [requests.data])
  const taskVMs = useMemo(() => (tasks.data ?? []).map(taskVM), [tasks.data])
  const participatingVMs = useMemo(() => (participating.data ?? []).map(reqVM), [participating.data])

  // «Все» = открытые задачи на мне + мои заявки + все документы с моим участием, без дублей
  // (задача вытесняет свою строку-заявку, заявка — свой дубль из participating; см. mergeByDoc).
  const allVMs = useMemo(() => mergeByDoc(taskVMs, [...reqVMs, ...participatingVMs]), [reqVMs, taskVMs, participatingVMs])
  // «Согласую / подписываю» — участие, где я не автор (см. signingOf).
  const signingVMs = useMemo(() => signingOf(taskVMs, reqVMs, participatingVMs), [reqVMs, taskVMs, participatingVMs])

  // Счётчики статусов по «Все» — для чипов карточки на Home (на /processes считаются по вкладке).
  const counts = useMemo(() => statusCounts(allVMs), [allVMs])

  return {
    reqVMs,
    taskVMs,
    participatingVMs,
    signingVMs,
    allVMs,
    counts,
    // Сырые query-результаты — для QueryState/isError на экранах.
    queries: { requests, tasks, participating },
  }
}
